import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      setFormStatus('idle');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d61m0v150i17hner1o8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] font-['Inter',sans-serif] text-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0B0F1A]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/5c03a520-f9b1-416e-9bc2-2ebf9ab9c281.png" 
              alt="Vnerd Logo" 
              className="h-14 w-auto"
            />
          </a>
          
          <nav className="hidden lg:flex items-center gap-8">
            <a href="#home" className="text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer">Home</a>
            <a href="#services" className="text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer">Services</a>
            <a href="#works" className="text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer">Works</a>
            <a href="#about" className="text-sm font-medium text-gray-300 hover:text-white transition-colors cursor-pointer">About</a>
            <a href="#contact" className="bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-lg hover:shadow-[#7C3AED]/30 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Contact
            </a>
          </nav>

          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center"
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl text-white`}></i>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-[#0B0F1A] z-40 lg:hidden pt-24 px-6">
          <nav className="flex flex-col space-y-6">
            <a href="#home" className="text-lg font-medium text-white hover:text-[#7C3AED]" onClick={() => setMenuOpen(false)}>Home</a>
            <a href="#services" className="text-lg font-medium text-white hover:text-[#7C3AED]" onClick={() => setMenuOpen(false)}>Services</a>
            <a href="#works" className="text-lg font-medium text-white hover:text-[#7C3AED]" onClick={() => setMenuOpen(false)}>Works</a>
            <a href="#about" className="text-lg font-medium text-white hover:text-[#7C3AED]" onClick={() => setMenuOpen(false)}>About</a>
            <a href="#contact" className="text-lg font-medium text-white hover:text-[#7C3AED]" onClick={() => setMenuOpen(false)}>Contact</a>
          </nav>
        </div>
      )}

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/45892e6d71e748886d1432e1d9630a47/a0fe09f896d9339baf88cf35b09ae3a3.png" 
            alt="Vnerd Hero"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F1A]/80 via-[#0B0F1A]/70 to-[#0B0F1A]"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-[#7C3AED]/20 backdrop-blur-sm border border-[#7C3AED]/30 rounded-full mb-8">
            <p className="text-sm text-[#22D3EE] font-medium">ベトナム最大級のクリエイターネットワーク</p>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="block">
              <span className="hidden md:inline">拡散力 × ビジュアル × 制作力</span>
              <span className="md:hidden">拡散力<br />ビジュアル<br />制作力</span>
            </span>
            <span className="block mt-3 bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] bg-clip-text text-transparent">
              <span className="hidden md:inline">次世代プロモーション</span>
              <span className="md:hidden">次世代<br />プロモーション</span>
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            クリエイター総数100名以上。<br />
            SNS広告、ライブ配信、イベント出演から、アパレル・グッズ制作、<br className="hidden md:block" />
            機材・サブカル商材・食品の輸入輸出まで一気通貫。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="#contact" 
              className="bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] text-white px-10 py-4 rounded-full font-medium text-lg hover:shadow-xl hover:shadow-[#7C3AED]/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </a>
            <a 
              href="#services" 
              className="bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-full font-medium text-lg hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer border border-white/20"
            >
              サービスを見る
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22D3EE] mb-2">100+</div>
              <div className="text-sm text-gray-400">クリエイター総数</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22D3EE] mb-2">50+</div>
              <div className="text-sm text-gray-400">企業実績</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22D3EE] mb-2">24h</div>
              <div className="text-sm text-gray-400">スピード対応</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22D3EE] mb-2">Global</div>
              <div className="text-sm text-gray-400">日本・海外対応</div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <a href="#services" className="text-white/70 hover:text-white transition-opacity cursor-pointer">
            <i className="ri-arrow-down-line text-3xl animate-bounce"></i>
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#0B0F1A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">SERVICES</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">提供サービス</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              企画から制作、配信、拡散、そして商流構築まで。すべてをワンストップで提供します。
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Service 1 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-megaphone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">SNS広告・PR</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Instagram、TikTok、YouTube等での商品PR、ブランドタイアップ。高いエンゲージメント率で確実にリーチ。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>インフルエンサーキャスティング</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>コンテンツ企画・制作</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>効果測定・レポート</span>
                </li>
              </ul>
            </div>

            {/* Service 2 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-live-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">ライブ配信</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                各種プラットフォームでのライブ配信企画・運営。リアルタイムでファンと繋がり、商品の魅力を伝えます。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>配信企画・台本作成</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>ライバーアサイン</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>配信サポート・分析</span>
                </li>
              </ul>
            </div>

            {/* Service 3 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-calendar-event-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">イベント出演</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                展示会、ゲームショー、企業イベント等へのコスプレイヤー派遣。会場を華やかに演出し、集客力を高めます。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>キャスト選定・手配</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>衣装・メイク対応</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>当日運営サポート</span>
                </li>
              </ul>
            </div>

            {/* Service 4 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-t-shirt-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">アパレル制作</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                オリジナルアパレルの企画・デザイン・製造。コスプレ衣装からブランドアパレルまで幅広く対応。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>デザイン企画</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>パターン制作・縫製</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>小ロット対応可能</span>
                </li>
              </ul>
            </div>

            {/* Service 5 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-palette-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">グラフィックデザイン</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                ロゴ、バナー、パッケージ、SNS素材等のデザイン制作。ブランドイメージを視覚的に表現します。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>ブランディングデザイン</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>SNS用クリエイティブ</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>印刷物デザイン</span>
                </li>
              </ul>
            </div>

            {/* Service 6 */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-gift-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">グッズ制作</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                アクリルスタンド、缶バッジ、ステッカー等のオリジナルグッズ制作。イベント物販やファングッズに最適。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>多様なグッズ対応</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>デザイン込みOK</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>短納期対応可能</span>
                </li>
              </ul>
            </div>

            {/* Service 7 - NEW: Import/Export */}
            <div className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                <i className="ri-ship-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 group-hover:text-[#22D3EE] transition-colors">輸入・輸出（越境支援）</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                機材、サブカル商材、食品の輸入輸出を支援。商流設計から通関手配まで伴走します。
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>機材・イベント用品</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>アパレル・グッズ商材</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#22D3EE]"></i>
                  <span>食品（規制対応前提）</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-gray-500 leading-relaxed">
                  ※対応可否は国・品目・規制により異なります
                </p>
              </div>
            </div>
          </div>

          {/* Import/Export Details Section */}
          <div className="mt-20 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 md:p-12 rounded-2xl border border-white/5">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold mb-4">輸入・輸出支援の詳細</h3>
                <p className="text-gray-400 max-w-3xl mx-auto">
                  プロモーションに加え、機材・サブカル商材・食品の輸入輸出も支援します。<br />
                  国・品目・規制により対応範囲が異なるため、まずは商材情報と希望国をご共有ください。
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Equipment */}
                <div className="bg-[#0B0F1A]/50 p-6 rounded-xl border border-white/5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7C3AED]/20 rounded-lg mb-4">
                    <i className="ri-camera-line text-xl text-[#22D3EE]"></i>
                  </div>
                  <h4 className="text-lg font-bold mb-3">機材の輸入・輸出</h4>
                  <p className="text-sm text-gray-400 mb-4">撮影・配信機材、イベント機材、照明/音響関連など</p>
                  <ul className="space-y-2 text-xs text-gray-500">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>商材要件整理（型番/仕様/用途/数量）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>見積取得・サプライヤー調整</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>梱包・輸送手配（海上/航空）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>通関書類の準備支援</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>納品・初期動作確認の段取り</span>
                    </li>
                  </ul>
                </div>

                {/* Subculture Goods */}
                <div className="bg-[#0B0F1A]/50 p-6 rounded-xl border border-white/5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7C3AED]/20 rounded-lg mb-4">
                    <i className="ri-shopping-bag-line text-xl text-[#22D3EE]"></i>
                  </div>
                  <h4 className="text-lg font-bold mb-3">サブカル商材の輸入・輸出</h4>
                  <p className="text-sm text-gray-400 mb-4">アパレル/グッズ、イベント向け販促物、コラボ商材など</p>
                  <ul className="space-y-2 text-xs text-gray-500">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>商流設計（製造→輸送→販売チャネル）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>品質/仕様確認、サンプル手配</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>ブランド/コラボの展開設計（PR連携）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>越境販売・イベント販売のオペ設計</span>
                    </li>
                  </ul>
                </div>

                {/* Food */}
                <div className="bg-[#0B0F1A]/50 p-6 rounded-xl border border-white/5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7C3AED]/20 rounded-lg mb-4">
                    <i className="ri-restaurant-line text-xl text-[#22D3EE]"></i>
                  </div>
                  <h4 className="text-lg font-bold mb-3">食品の輸入・輸出</h4>
                  <p className="text-sm text-gray-400 mb-4">加工食品、菓子、飲料など（規制対応を前提）</p>
                  <ul className="space-y-2 text-xs text-gray-500">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>輸出入可否の事前確認（HSコード/成分/表示要件）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>必要書類の確認・準備支援</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>物流/温度帯/賞味期限を踏まえた輸送設計</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>通関・検疫の手配支援</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-[#22D3EE] mt-0.5 flex-shrink-0"></i>
                      <span>到着後の国内流通/販売スキーム検討</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-xs text-yellow-400/80 leading-relaxed">
                      ⚠️ 食品は国別規制、表示要件、衛生・検疫、成分規制等により取り扱い可否が変動します。案件により専門業者と連携します。
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-[#7C3AED]/10 border border-[#7C3AED]/20 rounded-xl">
                <div className="flex items-start gap-4">
                  <i className="ri-information-line text-2xl text-[#22D3EE] flex-shrink-0 mt-1"></i>
                  <div>
                    <h5 className="font-bold mb-2">重要事項</h5>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>• 対応可否は国・品目・規制により異なります</li>
                      <li>• 必要に応じて通関/検疫/認証の専門パートナーと連携します</li>
                      <li>• 食品は特に規制が多く、事前確認を必須とします</li>
                      <li>• まずは商材情報（品目/数量/希望国/納期）をご共有ください</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <a 
              href="#contact" 
              className="inline-block bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] text-white px-10 py-4 rounded-full font-medium hover:shadow-xl hover:shadow-[#7C3AED]/40 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              サービスについて相談する
            </a>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section id="works" className="py-24 bg-gradient-to-b from-[#0B0F1A] to-[#1a1f2e]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">WORKS</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">実績紹介</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              日本・海外企業との豊富な実績。信頼と実績で選ばれています。
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                img: 'https://public.readdy.ai/ai/img_res/05d803ec88ed7d9065c4441ca1e45e08.jpg',
                title: 'ゲーム新作プロモーション',
                category: 'SNS広告・イベント',
                desc: '大手ゲーム会社の新作タイトルPR。SNS投稿とイベント出演で高いエンゲージメントを獲得。'
              },
              {
                img: 'https://readdy.ai/api/search-image?query=professional%20livestream%20setup%20with%20cosplay%20streamer%20in%20modern%20studio%20environment%20featuring%20professional%20lighting%20equipment%20and%20clean%20background%20showing%20high%20quality%20broadcast%20production%20for%20corporate%20brand%20promotion%20and%20product%20showcase%20with%20engaging%20presentation&width=800&height=600&seq=vnerd003&orientation=landscape',
                title: 'コスメブランドライブ配信',
                category: 'ライブ配信',
                desc: '新作コスメの発売記念ライブ配信。視聴者数10万人超え、高い購買転換率を実現。'
              },
              {
                img: 'https://readdy.ai/api/search-image?query=elegant%20fashion%20apparel%20display%20featuring%20modern%20streetwear%20and%20cosplay%20inspired%20clothing%20designs%20on%20professional%20mannequins%20in%20clean%20studio%20setting%20with%20soft%20lighting%20showcasing%20high%20quality%20garment%20construction%20and%20contemporary%20design%20aesthetics%20for%20brand%20presentation&width=800&height=600&seq=vnerd004&orientation=landscape',
                title: 'オリジナルアパレルライン',
                category: 'アパレル制作',
                desc: 'クリエイターコラボアパレルの企画・製造。完売御礼の人気シリーズに。'
              },
              {
                img: 'https://readdy.ai/api/search-image?query=professional%20graphic%20design%20mockup%20display%20featuring%20modern%20brand%20identity%20materials%20including%20business%20cards%20posters%20and%20digital%20assets%20with%20clean%20contemporary%20aesthetic%20and%20vibrant%20color%20scheme%20suitable%20for%20entertainment%20and%20creative%20industry%20branding&width=800&height=600&seq=vnerd006&orientation=landscape',
                title: '企業ブランディング',
                category: 'グラフィックデザイン',
                desc: 'スタートアップ企業のロゴ・VI制作。ブランドイメージの確立に貢献。'
              },
              {
                img: 'https://readdy.ai/api/search-image?query=colorful%20collection%20of%20anime%20merchandise%20and%20character%20goods%20including%20acrylic%20stands%20badges%20and%20stickers%20displayed%20professionally%20on%20clean%20white%20background%20showing%20high%20quality%20printing%20and%20vibrant%20colors%20suitable%20for%20fan%20merchandise%20and%20event%20sales&width=800&height=600&seq=vnerd007&orientation=landscape',
                title: 'オリジナルグッズ展開',
                category: 'グッズ制作',
                desc: 'イベント物販用グッズの企画・製造。デザインから納品まで一貫対応。'
              },
              {
                img: 'https://readdy.ai/api/search-image?query=professional%20international%20shipping%20and%20logistics%20scene%20with%20neatly%20stacked%20cardboard%20boxes%20and%20packages%20in%20modern%20warehouse%20facility%20showing%20organized%20inventory%20management%20and%20cross%20border%20trade%20operations%20with%20clean%20industrial%20aesthetic%20suitable%20for%20business%20presentation&width=800&height=600&seq=vnerd008&orientation=landscape',
                title: '越境商材支援',
                category: '輸入・輸出',
                desc: 'サブカル商材の日本向け輸出支援。商流設計から通関手配まで一貫サポート。'
              }
            ].map((work, idx) => (
              <div 
                key={idx}
                className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#7C3AED]/10 cursor-pointer"
              >
                <div className="w-full h-64 overflow-hidden">
                  <img 
                    src={work.img} 
                    alt={work.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#7C3AED]/20 text-[#22D3EE] text-xs rounded-full mb-3">
                    {work.category}
                  </span>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-[#22D3EE] transition-colors">
                    {work.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {work.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-400 mb-6">その他多数の実績がございます。詳細はお問い合わせください。</p>
            <a 
              href="#contact" 
              className="inline-block bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-full font-medium hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer border border-white/20"
            >
              実績について問い合わせる
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">PROCESS</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">ご依頼の流れ</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              スピーディーかつ丁寧に対応。初回相談から納品まで安心のサポート体制。
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'お問い合わせ',
                  desc: 'フォームまたはお電話でお気軽にご相談ください。24時間以内に返信いたします。'
                },
                {
                  step: '02',
                  title: 'ヒアリング',
                  desc: 'ご要望、予算、スケジュール等を詳しくお伺いします。オンライン面談も可能です。'
                },
                {
                  step: '03',
                  title: '企画提案',
                  desc: '最適なクリエイター選定と企画案をご提案。修正は何度でも対応いたします。'
                },
                {
                  step: '04',
                  title: '契約・制作',
                  desc: 'ご納得いただけましたら契約締結。制作・撮影・配信等を進行します。'
                },
                {
                  step: '05',
                  title: '納品・レポート',
                  desc: '成果物の納品と効果測定レポートをお渡しします。アフターフォローも万全です。'
                }
              ].map((process, idx) => (
                <div 
                  key={idx}
                  className="flex gap-6 items-start group"
                >
                  <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                    {process.step}
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#22D3EE] transition-colors">
                      {process.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {process.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-[#1a1f2e] to-[#0B0F1A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">ABOUT</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Vnerdについて</h2>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/45892e6d71e748886d1432e1d9630a47/96cd1cfee7f36b8fdb9c9c55381f4551.jpeg" 
                  alt="Vnerd Team"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="space-y-6">
                <h3 className="text-3xl font-bold leading-tight">
                  ベトナム最大級の<br />
                  クリエイターネットワーク
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  Vnerdは、ベトナムを拠点にクリエイター総数100名以上を擁する次世代プロモーションチームです。
                </p>
                <p className="text-gray-400 leading-relaxed">
                  SNS広告、ライブ配信、イベント出演といったプロモーション活動から、アパレル企画・制作、グラフィックデザイン、オリジナルグッズ制作、さらには機材・サブカル商材・食品の輸入輸出まで、すべてを一気通貫で提供。
                </p>
                <p className="text-gray-400 leading-relaxed">
                  日本・海外企業の広告・PR案件に多数の実績を持ち、「拡散力 × ビジュアル × 制作力」に加え、越境ビジネスの実装まで支援します。
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 md:p-12 rounded-2xl border border-white/5">
              <h3 className="text-2xl font-bold mb-8 text-center">会社情報</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">会社名</div>
                    <div className="text-lg font-medium">Vnerd（C＆T）</div>
                  </div>
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">所在地</div>
                    <div className="text-sm leading-relaxed">
                      8a/a3 Đ. Thái Văn Lung, Bến Nghé,<br />
                      Quan 1, Ho Chi Minh, Vietnam
                    </div>
                  </div>
                </div>
                <div>
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">電話</div>
                    <div className="text-lg font-medium">+84-909-012-811</div>
                  </div>
                  <div className="mb-6">
                    <div className="text-sm text-gray-500 mb-2">事業内容</div>
                    <div className="text-sm leading-relaxed">
                      クリエイターマネジメント<br />
                      SNS広告・PR事業<br />
                      ライブ配信事業<br />
                      イベント企画・運営<br />
                      アパレル・グッズ制作<br />
                      輸入・輸出/越境支援
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">よくある質問</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: '最低予算はいくらからですか？',
                a: 'プロジェクトの内容により異なりますが、小規模案件は10万円程度から対応可能です。まずはお気軽にご相談ください。'
              },
              {
                q: '日本語でのやり取りは可能ですか？',
                a: 'はい、日本語対応スタッフがおりますので、日本語でのコミュニケーションに問題ございません。'
              },
              {
                q: '納期はどのくらいかかりますか？',
                a: '内容により異なりますが、SNS投稿であれば最短1週間、イベント出演は2週間〜、制作物は3週間〜が目安です。お急ぎの場合もご相談ください。'
              },
              {
                q: '契約書や請求書の発行は可能ですか？',
                a: 'はい、正式な契約書・請求書・領収書等の発行に対応しております。企業様との取引実績も豊富です。'
              },
              {
                q: '著作権や肖像権の取り扱いはどうなりますか？',
                a: '契約時に明確に取り決めを行います。二次利用の範囲等もご相談に応じて柔軟に対応いたします。'
              },
              {
                q: '輸入・輸出はどの国に対応していますか？',
                a: '主に日本・ベトナム間の輸出入を支援していますが、他国についてもご相談ください。対応可否は品目・規制・物流状況により異なります。'
              },
              {
                q: '食品の輸入は本当に可能ですか？',
                a: '食品は国別規制、表示要件、衛生・検疫、成分規制等により取り扱い可否が大きく変動します。まずは商材情報（品目/成分/製造国/温度帯/賞味期限）をご共有いただき、事前確認を行います。案件により専門業者と連携します。'
              }
            ].map((faq, idx) => (
              <details 
                key={idx}
                className="group bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] rounded-xl border border-white/5 hover:border-[#7C3AED]/30 transition-all duration-300"
              >
                <summary className="p-6 cursor-pointer list-none flex items-center justify-between">
                  <span className="font-medium pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-xl text-[#22D3EE] group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-6 text-gray-400 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-[#1a1f2e] to-[#0B0F1A]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#22D3EE] mb-4 block">CONTACT</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">お問い合わせ</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              ご相談・お見積りは無料です。お気軽にお問い合わせください。
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-4">フォームから</h3>
                <p className="text-gray-400 text-sm mb-6">
                  下記フォームよりお問い合わせください。24時間以内に返信いたします。
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 rounded-2xl border border-white/5">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#22D3EE] rounded-xl mb-6">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-4">お電話で</h3>
                <p className="text-gray-400 text-sm mb-4">
                  お急ぎの方はお電話でもお問い合わせいただけます。
                </p>
                <a href="tel:+84909012811" className="text-[#22D3EE] font-medium text-lg hover:underline cursor-pointer">
                  +84-909-012-811
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1f2e] to-[#0B0F1A] p-8 md:p-12 rounded-2xl border border-white/5">
              <form className="space-y-6" data-readdy-form id="vnerd-contact-form" onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">お名前 <span className="text-red-400">*</span></label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="山田太郎"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">会社名</label>
                    <input 
                      type="text" 
                      name="company"
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="株式会社〇〇"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">メールアドレス <span className="text-red-400">*</span></label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="example@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">電話番号</label>
                    <input 
                      type="tel" 
                      name="phone"
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="090-1234-5678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">お問い合わせ種別 <span className="text-red-400">*</span></label>
                  <select 
                    name="inquiry_type"
                    required
                    className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="SNS広告・PR">SNS広告・PR</option>
                    <option value="ライブ配信">ライブ配信</option>
                    <option value="イベント出演">イベント出演</option>
                    <option value="アパレル制作">アパレル制作</option>
                    <option value="グラフィックデザイン">グラフィックデザイン</option>
                    <option value="グッズ制作">グッズ制作</option>
                    <option value="輸入・輸出（機材）">輸入・輸出（機材）</option>
                    <option value="輸入・輸出（サブカル商材）">輸入・輸出（サブカル商材）</option>
                    <option value="輸入・輸出（食品）">輸入・輸出（食品）</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">希望国（輸入・輸出の場合）</label>
                    <input 
                      type="text" 
                      name="target_country"
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="例：日本、ベトナム"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">数量・重量（輸入・輸出の場合）</label>
                    <input 
                      type="text" 
                      name="quantity_weight"
                      className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white"
                      placeholder="例：100個、50kg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">お問い合わせ内容 <span className="text-red-400">*</span></label>
                  <textarea 
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-[#0B0F1A] border border-white/10 rounded-lg focus:border-[#7C3AED] focus:outline-none transition-colors text-white resize-none"
                    placeholder="ご相談内容、予算、希望納期等をご記入ください（500文字以内）&#10;&#10;※食品の場合は、品目/成分/製造国/温度帯/賞味期限等の情報もご記入ください"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">
                    ※食品の輸入・輸出をご希望の場合は、成分表・ラベル案・温度帯・賞味期限等の情報を添えてご相談ください
                  </p>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-6 py-4 rounded-lg text-center">
                    送信が完了しました。24時間以内にご返信いたします。
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-6 py-4 rounded-lg text-center">
                    送信に失敗しました。もう一度お試しください。
                  </div>
                )}

                <div className="text-center space-y-4">
                  <p className="text-xs text-gray-400">
                    <a href="/privacy" className="text-[#22D3EE] hover:underline cursor-pointer">個人情報の取り扱いについて</a>をご確認の上、送信してください。
                  </p>
                  <button 
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] text-white px-12 py-4 rounded-full font-medium text-lg hover:shadow-xl hover:shadow-[#7C3AED]/40 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '送信中...' : '送信する'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B0F1A] border-t border-white/5 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
            <div>
              <a href="/" className="inline-block mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/5c03a520-f9b1-416e-9bc2-2ebf9ab9c281.png" 
                  alt="Vnerd Logo" 
                  className="h-14 w-auto"
                />
              </a>
              <p className="text-sm text-gray-500 max-w-xs">
                ベトナム最大級のコスプレイヤー／ライバーネットワーク
              </p>
            </div>

            <nav className="flex flex-wrap gap-6 text-sm">
              <a href="#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</a>
              <a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Services</a>
              <a href="#works" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Works</a>
              <a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About</a>
              <a href="#contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Privacy</a>
            </nav>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center md:items-end gap-4 text-sm text-gray-500">
            <div className="space-y-2 text-center md:text-left">
              <p>8a/a3 Đ. Thái Văn Lung, Bến Nghé, Quan 1, Ho Chi Minh, Vietnam</p>
              <p>Tel: +84-909-012-811</p>
            </div>
            <div className="flex items-center gap-2">
              <p>&copy; 2024 Vnerd. All Rights Reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
